import { Dispatch, FormEvent, ForwardedRef, SetStateAction, useContext, useEffect, useRef, useState } from "react";

import { FileUploadHandle, SelectData } from "../../../../../../../models";
import {
  GetProjectDetailsResponse,
  searchProjectDocumentTypes,
  searchProjectDocumentTypeVariants,
  searchProjectOrganisations,
} from "../../../../../../../service/query";
import { ServiceError } from "../../../../../../../service/Shared";
import { useAuth } from "../../../../../../../useAuth";
import { useIsLoadingWrapper } from "../../../../../../../utils";
import { dataGridMapFilterCriteria } from "../../../../../../../widget";
import { ProjectContext } from "../../../ProjectContext";

interface UseUploadLocationDocumentModalProps {
  show: boolean;
  closeModal: () => void;
  saveBoundaryFile: (boundaryFile: File, visibility: boolean, selectedOrganisations: string[]) => void;
  drawShapeJson: string;
}

interface UseUploadLocationDocumentModalReturnData {
  fileUploadRef: ForwardedRef<FileUploadHandle>;
  projectDetails: GetProjectDetailsResponse | undefined;
  maxFileSize?: number;
  selectedDocumentVariant?: string;
  selectedDocumentType?: string;
  isHandleSubmitLoading: boolean;
  setFiles: Dispatch<SetStateAction<File[] | undefined>>;
  handleCancel: () => void;
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  errors: ServiceError[];
  relatedOrganisations?: SelectData;
  visibility: boolean | undefined;
  setVisibility: Dispatch<SetStateAction<boolean | undefined>>;
  selectedOrganisations: string[] | undefined;
  setSelectedOrganisations: Dispatch<SetStateAction<string[] | undefined>>;
}

export const useUploadLocationDocumentModal = ({
  show,
  closeModal,
  saveBoundaryFile,
  drawShapeJson,
}: UseUploadLocationDocumentModalProps): UseUploadLocationDocumentModalReturnData => {
  const fileUploadRef = useRef<FileUploadHandle>(null);
  const { currentOrganisationUuid } = useAuth();
  const { projectDetails } = useContext(ProjectContext);
  const [errors, setErrors] = useState<ServiceError[]>([]);
  const maxFileSize = 20; // 20mb

  const [relatedOrganisations, setRelatedOrganisations] = useState<SelectData>();
  const [visibility, setVisibility] = useState<boolean | undefined>();
  const [selectedOrganisations, setSelectedOrganisations] = useState<string[] | undefined>();
  const [selectedDocumentType, setSelectedDocumentType] = useState<string>();
  const [selectedDocumentVariant, setSelectedDocumentVariant] = useState<string>();
  const [files, setFiles] = useState<File[]>();
  const [isHandleSubmitLoading, setIsHandleSubmitLoading] = useState(false);

  const handleCancel = (): void => {
    if (fileUploadRef.current) fileUploadRef.current.clearInput();
    setErrors([]);
    closeModal();
  };

  useEffect(() => {
    if (show) {
      let filterCriteria = dataGridMapFilterCriteria([]);

      filterCriteria.results = {
        name: {
          operator: "eq",
          value: "Map of Site",
        },
        standard: {
          standardUuid: {
            operator: "in",
            value:
              projectDetails !== undefined && projectDetails?.standard !== undefined
                ? `${projectDetails?.standard.uuid}, null`
                : null,
          },
        },
        isGenerated: {
          operator: "eq",
          value: false,
        },
      };

      searchProjectDocumentTypes({
        paging: { beforeCursor: null, afterCursor: null, limit: 50 },
        filter: filterCriteria,
      }).then((response) => {
        if (response.data?.results && response.data?.results.length > 0) {
          setSelectedDocumentType(response.data.results[0].name);
          filterCriteria = dataGridMapFilterCriteria([]);

          filterCriteria.results = {
            name: {
              operator: "eq",
              value: "Boundary",
            },
            typeUuid: {
              operator: "eq",
              value: response.data.results[0].uuid,
            },
          };

          searchProjectDocumentTypeVariants({
            paging: { beforeCursor: null, afterCursor: null, limit: 50 },
            filter: filterCriteria,
          }).then((res) => {
            if (res.data?.results && res.data?.results.length > 0) {
              setSelectedDocumentVariant(res.data.results[0].name);
            }
          });
        }
      });

      if (projectDetails?.uuid) {
        filterCriteria = dataGridMapFilterCriteria([]);
        filterCriteria.results = {
          role: {
            operator: "in",
            value: "Developer, Development Manager, Guest - Write, Guest - Read, Asset Manager",
          },
        };
        searchProjectOrganisations({
          projectUuid: projectDetails.uuid,
          paging: { beforeCursor: null, afterCursor: null, limit: 50 },
          filter: filterCriteria,
        }).then((response) => {
          const guestOrganistions = response.data?.results.filter(
            (f) => f.organisationUuid !== currentOrganisationUuid
          );
          setRelatedOrganisations(
            guestOrganistions?.map((r) => ({ key: r.organisationUuid, value: r.displayName })) as SelectData
          );
        });
      }
    }
  }, [show]);

  const handleSubmit = useIsLoadingWrapper(async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setErrors([]);

    if (files === undefined && !drawShapeJson) {
      setErrors([
        {
          code: "MANDATORY_FIELDS",
          message: `Please complete the mandatory fields: File`,
        },
      ]);
      return;
    }
    let inputFile: File;
    if (drawShapeJson) {
      const blob = new Blob([drawShapeJson || "{}"], { type: "text/plain" });
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
        now.getDate()
      ).padStart(2, "0")}-${String(now.getHours()).padStart(2, "0")}-${String(now.getMinutes()).padStart(2, "0")}`;
      inputFile = new File([blob], `${projectDetails?.displayName}_BoundaryGeoJson_${timestamp}.json`, {
        type: "text/plain",
      });
    } else {
      // eslint-disable-next-line prefer-destructuring
      inputFile = files ? files[0] : new File([], "");
    }
    let inputSelectedOrganisations = visibility || false ? selectedOrganisations || [] : [];
    if (visibility && currentOrganisationUuid && !inputSelectedOrganisations.includes(currentOrganisationUuid)) {
      inputSelectedOrganisations = [...inputSelectedOrganisations, currentOrganisationUuid];
    }
    saveBoundaryFile(inputFile, visibility || false, inputSelectedOrganisations);
  }, setIsHandleSubmitLoading);

  return {
    fileUploadRef,
    projectDetails,
    maxFileSize,
    selectedDocumentVariant,
    selectedDocumentType,
    isHandleSubmitLoading,
    setFiles,
    handleCancel,
    handleSubmit,
    errors,
    relatedOrganisations,
    visibility,
    setVisibility,
    selectedOrganisations,
    setSelectedOrganisations,
  };
};
